import React from 'react';
import { motion } from 'framer-motion';
import {
  BeakerIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/solid';

import { useSelector, useDispatch } from 'react-redux';
import { setEnvironment, type Environment } from '../../redux/slices/authSlice';
import { fetchRestaurantsRequest } from '../../redux/slices/restaurantSlice';
import type { RootState } from '../../redux/store';

type EnvironmentConfig = {
  key: Environment;
  label: string;
  icon: React.ElementType;
  activeBg: string;
  inactiveIcon: string;
};

const environments: EnvironmentConfig[] = [
  {
    key: 'sandbox',
    label: 'Sandbox',
    icon: BeakerIcon,
    activeBg: 'bg-amber-500',
    inactiveIcon: 'text-amber-500',
  },
  {
    key: 'production',
    label: 'Production',
    icon: ShieldCheckIcon,
    activeBg: 'bg-emerald-600',
    inactiveIcon: 'text-emerald-600',
  },
];

interface EnvironmentToggleProps {
  skipAccessControl?: boolean;
}

export const EnvironmentToggle: React.FC<EnvironmentToggleProps> = ({ 
  skipAccessControl = false 
}) => {
  const dispatch = useDispatch();
  const { user, environment: currentEnvironment } = useSelector(
    (state: RootState) => state.auth
  );

  // Determine available environments
  const availableEnvironments = skipAccessControl
    ? environments  // Skip API key checks - show all options
    : environments.filter((env) => {
        // Access control - check API keys
        if (env.key === 'sandbox') return !!user?.accountId;
        if (env.key === 'production') return !!user?.prodAccountId;
        return false;
      });

  const hasMultipleEnvironments = availableEnvironments.length > 1;
  const hasSingleEnvironment = availableEnvironments.length === 1;

  const handleEnvironmentChange = (mode: Environment) => {
    if (mode !== currentEnvironment) {
      dispatch(setEnvironment(mode));
      dispatch(fetchRestaurantsRequest());
    }
  };

  // ---------------------------
  // SINGLE ENVIRONMENT BADGE
  // ---------------------------
  if (hasSingleEnvironment) {
    const environment = availableEnvironments[0];
    const Icon = environment.icon;

    return (
      <div className="flex w-full items-center justify-center px-3 sm:px-4">
        <div
          className="
            flex items-center gap-2
            rounded-full
            border border-gray-200
            bg-gray-50
            px-4 py-2
            sm:px-5 sm:py-2.5
            shadow-sm
          "
        >
          <Icon className={`h-4 w-4 sm:h-5 sm:w-5 ${environment.inactiveIcon}`} />
          <span
            className="
              text-xs
              sm:text-sm
              font-semibold
              tracking-wide
              text-gray-700
            "
          >
            {environment.label}
          </span>
        </div>
      </div>
    );
  }

  // ---------------------------
  // TOGGLE SWITCH
  // ---------------------------
  if (hasMultipleEnvironments) {
    return (
      <div className="flex w-full items-center justify-center px-3 sm:px-4">
        <div
          className="
            relative
            flex w-full max-w-xs sm:max-w-sm
            items-center
            rounded-full
            border border-[var(--border-color)]
            bg-[var(--bg-secondary)]
            p-1
            shadow-md
            backdrop-blur-md
          "
        >
          {/* Animated Active Background */}
          <motion.div
            layout
            transition={{
              type: 'spring',
              stiffness: 420,
              damping: 30,
            }}
            className={`
              absolute top-1 bottom-1
              w-[calc(50%-4px)]
              rounded-full
              shadow-sm
              ${
                currentEnvironment === 'sandbox'
                  ? 'left-1 bg-amber-500'
                  : 'left-[calc(50%+2px)] bg-emerald-600'
              }
            `}
          />

          {availableEnvironments.map((environment) => {
            const isActive = currentEnvironment === environment.key;
            const Icon = environment.icon;

            return (
              <button
                key={environment.key}
                type="button"
                onClick={() =>
                  handleEnvironmentChange(environment.key)
                }
                aria-label={environment.label}
                aria-pressed={isActive}
                className={`
                  relative z-10
                  flex flex-1 items-center justify-center
                  rounded-full
                  px-3 py-2.5
                  sm:px-5 sm:py-3
                  transition-all duration-300
                  focus:outline-none
                  focus-visible:ring-2
                  focus-visible:ring-offset-2
                  focus-visible:ring-amber-400
                  ${
                    isActive
                      ? 'text-white'
                      : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                  }
                `}
              >
                <motion.div
                  whileTap={{ scale: 0.95 }}
                  className="
                    flex items-center gap-1.5 sm:gap-2
                  "
                >
                  <Icon
                    className={`
                      h-4 w-4
                      sm:h-5 sm:w-5
                      transition-colors duration-300
                      ${
                        isActive
                          ? 'text-white'
                          : environment.inactiveIcon
                      }
                    `}
                  />
                  <span
                    className="
                      text-xs
                      sm:text-sm
                      md:text-[15px]
                      font-semibold
                      tracking-wide
                      whitespace-nowrap
                    "
                  >
                    {environment.label}
                  </span>
                </motion.div>
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // No environments available
  return null;
};
